import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Mail, FileText, Briefcase, Send, Plus, RefreshCw, Trash2, X, BookOpen, Edit2, Share2, Download, Link2 } from "lucide-react";
import Seo from "../components/Seo";

const TOKEN_KEY = "b7_admin_token";
const SPECIALISMS = [
  "Quantity Surveying",
  "Commercial, Contracts & Claims",
  "Dispute Resolution",
  "Planning",
  "Programme & Project Management",
];

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` };
}

function fmt(d) {
  try { return new Date(d).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return d; }
}

function fmtDate(d) {
  try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

function LoginScreen({ onLogin }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password: pwd });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch {
      toast.error("Invalid password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0E1530] p-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-md bg-white p-10 sm:p-12">
        <img src="/brand/b7-logo-transparent.png" alt="B7" className="h-12 w-auto mb-8" />
        <div className="overline">Admin</div>
        <h1 className="font-serif text-3xl mt-3">Sign in</h1>
        <p className="text-sm text-[#5C5C5C] mt-2">Restricted area — B7 staff only.</p>
        <input
          type="password"
          required
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="Password"
          className="field mt-8"
          data-testid="admin-password-input"
          autoFocus
        />
        <button type="submit" disabled={loading} className="btn-primary mt-8 w-full justify-center" data-testid="admin-login-button">
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[#E2E0D9] p-6 flex items-center justify-between">
      <div>
        <div className="overline">{label}</div>
        <div className="font-serif text-4xl mt-2">{value}</div>
      </div>
      <Icon size={28} className="text-[#1E2A78]" />
    </div>
  );
}

function VacancyForm({ onClose, onSaved, initial }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    role_title: initial?.role_title || "",
    company_name: initial?.company_name || "",
    specialism: initial?.specialism || SPECIALISMS[0],
    location: initial?.location || "",
    region: initial?.region || "UK & Europe",
    contract_type: initial?.contract_type || "Permanent",
    salary_range: initial?.salary_range || "",
    start_date: initial?.start_date || "",
    description: initial?.description || "",
    apply_email: initial?.apply_email || "",
  });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.apply_email) delete payload.apply_email;
      if (isEdit) {
        await api.patch(`/admin/vacancies/${initial.id}`, payload, { headers: authHeader() });
        toast.success("Vacancy updated.");
      } else {
        await api.post("/admin/vacancies", payload, { headers: authHeader() });
        toast.success("Vacancy posted — live for 30 days.");
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEdit ? "Failed to update vacancy." : "Failed to post vacancy."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="vacancy-form-modal">
      <div className="bg-white w-full max-w-2xl p-8 sm:p-10 my-10 relative">
        <button onClick={onClose} className="absolute top-5 right-5 text-[#5C5C5C] hover:text-[#1E2A78]" data-testid="vacancy-form-close" aria-label="Close">
          <X size={20} />
        </button>
        <div className="overline">{isEdit ? "Edit vacancy" : "New vacancy"}</div>
        <h2 className="font-serif text-3xl mt-3">{isEdit ? "Edit vacancy" : "Post a vacancy"}</h2>
        <p className="text-sm text-[#5C5C5C] mt-2">{isEdit ? "Update the details below. Changes go live immediately." : "Live for 30 days. You will receive an email reminder to refresh on expiry."}</p>

        <form onSubmit={submit} className="space-y-6 mt-8" data-testid="admin-vacancy-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <input className="field" placeholder="Role title *" required value={form.role_title} onChange={update("role_title")} data-testid="admin-vacancy-role-input" />
            <input className="field" placeholder="Company / Client name *" required value={form.company_name} onChange={update("company_name")} data-testid="admin-vacancy-company-input" />
            <select className="field" value={form.specialism} onChange={update("specialism")} data-testid="admin-vacancy-specialism-select">
              {SPECIALISMS.map((s) => <option key={s}>{s}</option>)}
            </select>
            <select className="field" value={form.region} onChange={update("region")} data-testid="admin-vacancy-region-select">
              <option>UK & Europe</option>
              <option>Middle East</option>
            </select>
            <input className="field" placeholder="Location (city) *" required value={form.location} onChange={update("location")} data-testid="admin-vacancy-location-input" />
            <select className="field" value={form.contract_type} onChange={update("contract_type")} data-testid="admin-vacancy-contract-select">
              <option>Permanent</option>
              <option>Contract</option>
              <option>Day Rate</option>
            </select>
            <input className="field" placeholder="Salary / day rate range" value={form.salary_range} onChange={update("salary_range")} data-testid="admin-vacancy-salary-input" />
            <input className="field" placeholder="Start date" value={form.start_date} onChange={update("start_date")} data-testid="admin-vacancy-start-input" />
          </div>
          <input className="field" placeholder="Apply email (where candidates send applications)" type="email" value={form.apply_email} onChange={update("apply_email")} data-testid="admin-vacancy-apply-input" />
          <textarea className="field" placeholder="Role description *" required rows={6} value={form.description} onChange={update("description")} data-testid="admin-vacancy-description-input" />

          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="admin-vacancy-cancel">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary" data-testid="admin-vacancy-submit">
              {loading ? (isEdit ? "Saving..." : "Posting...") : (isEdit ? "Save changes" : "Post vacancy")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("vacancies");
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [cvs, setCvs] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [applications, setApplications] = useState([]);
  const [insights, setInsights] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingVacancy, setEditingVacancy] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);

  const loadAll = async () => {
    try {
      const [s, c, v, cv, ap, ins, nl] = await Promise.all([
        api.get("/admin/stats", { headers: authHeader() }),
        api.get("/admin/contacts", { headers: authHeader() }),
        api.get("/admin/vacancies", { headers: authHeader() }),
        api.get("/admin/cvs", { headers: authHeader() }),
        api.get("/admin/applications", { headers: authHeader() }),
        api.get("/admin/insights", { headers: authHeader() }),
        api.get("/admin/newsletter", { headers: authHeader() }),
      ]);
      setStats(s.data); setContacts(c.data); setVacancies(v.data); setCvs(cv.data); setApplications(ap.data); setInsights(ins.data); setNewsletter(nl.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      }
    }
  };

  useEffect(() => { if (authed) loadAll(); }, [authed]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  const refresh = async (id) => {
    try {
      await api.post(`/admin/vacancies/${id}/refresh`, {}, { headers: authHeader() });
      toast.success("Vacancy refreshed — live for another 30 days.");
      loadAll();
    } catch { toast.error("Refresh failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this vacancy?")) return;
    try {
      await api.delete(`/admin/vacancies/${id}`, { headers: authHeader() });
      toast.success("Vacancy removed.");
      loadAll();
    } catch { toast.error("Delete failed."); }
  };

  const shareOnLinkedIn = (vacancy) => {
    const url = `${window.location.origin}/vacancies/${vacancy.id}`;
    const text = `${vacancy.role_title} — ${vacancy.company_name} · ${vacancy.location}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  const copyLink = async (vacancy) => {
    const url = `${window.location.origin}/vacancies/${vacancy.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const deleteInsight = async (id) => {
    if (!window.confirm("Delete this insight?")) return;
    try {
      await api.delete(`/admin/insights/${id}`, { headers: authHeader() });
      toast.success("Insight removed.");
      loadAll();
    } catch { toast.error("Delete failed."); }
  };

  const pullNewInsight = async () => {
    setInsightLoading(true);
    try {
      await api.post(`/admin/insights/refresh`, {}, { headers: authHeader() });
      toast.success("New insight pulled.");
      loadAll();
    } catch { toast.error("Pull failed."); }
    finally { setInsightLoading(false); }
  };

  if (!authed) return <><Seo title="Admin" path="/admin" /><LoginScreen onLogin={() => setAuthed(true)} /></>;

  return (
    <div className="min-h-screen bg-[#F4F3F0]" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard" path="/admin" />
      <header className="bg-[#0E1530] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/brand/b7-logo-transparent.png" alt="B7" className="h-10 w-auto brightness-0 invert" />
            <span className="overline-light">Admin Dashboard</span>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#2EA3F2]" data-testid="admin-logout-button">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-12">
            <Stat label="Active Vacancies" value={stats.vacancies_active} icon={Briefcase} />
            <Stat label="Expired" value={stats.vacancies_expired} icon={Briefcase} />
            <Stat label="Applications" value={stats.applications} icon={FileText} />
            <Stat label="CV Submissions" value={stats.cvs} icon={FileText} />
            <Stat label="Contacts" value={stats.contacts} icon={Mail} />
            <Stat label="Newsletter" value={stats.newsletter} icon={Send} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-b border-[#E2E0D9] mb-8">
          {[
            ["vacancies", `Vacancies (${vacancies.length})`],
            ["applications", `Applications (${applications.length})`],
            ["insights", `Insights (${insights.length})`],
            ["cvs", `CV Submissions (${cvs.length})`],
            ["contacts", `Contacts (${contacts.length})`],
            ["newsletter", `Newsletter (${newsletter.length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${
                tab === key ? "text-[#1E2A78] border-b-2 border-[#1E2A78]" : "text-[#5C5C5C] hover:text-[#1E2A78]"
              }`}
            >
              {label}
            </button>
          ))}
          <div className="ml-auto pb-2">
            {tab === "vacancies" && (
              <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="admin-vacancy-new-button">
                <Plus size={16} /> Post vacancy
              </button>
            )}
            {tab === "insights" && (
              <button onClick={pullNewInsight} disabled={insightLoading} className="btn-primary" data-testid="admin-insight-pull-button">
                <RefreshCw size={16} /> {insightLoading ? "Pulling…" : "Pull new insight"}
              </button>
            )}
          </div>
        </div>

        {tab === "vacancies" && (
          vacancies.length === 0 ? (
            <div className="bg-white border border-[#E2E0D9] p-10 text-center text-[#5C5C5C]" data-testid="vacancies-empty-state">
              No vacancies yet. Click <span className="text-[#1E2A78] font-semibold">Post vacancy</span> to add one.
            </div>
          ) : (
            <div className="bg-white border border-[#E2E0D9] overflow-x-auto" data-testid="admin-vacancies-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F4F3F0]">
                    <th className="text-left px-5 py-4 overline">Status</th>
                    <th className="text-left px-5 py-4 overline">Role</th>
                    <th className="text-left px-5 py-4 overline">Company</th>
                    <th className="text-left px-5 py-4 overline">Specialism</th>
                    <th className="text-left px-5 py-4 overline">Location</th>
                    <th className="text-left px-5 py-4 overline">Posted</th>
                    <th className="text-left px-5 py-4 overline">Expires</th>
                    <th className="text-left px-5 py-4 overline">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vacancies.map((v) => (
                    <tr key={v.id} className="border-t border-[#E2E0D9] align-top" data-testid={`admin-vacancy-row-${v.id}`}>
                      <td className="px-5 py-4">
                        <span className={`text-[10px] tracking-[0.18em] uppercase font-semibold px-2 py-1 ${
                          v.status === "active" ? "bg-[#E6F3FE] text-[#1E2A78]" : "bg-[#F4F3F0] text-[#5C5C5C]"
                        }`}>
                          {v.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-semibold">{v.role_title}</td>
                      <td className="px-5 py-4">{v.company_name}</td>
                      <td className="px-5 py-4 text-[#5C5C5C]">{v.specialism}</td>
                      <td className="px-5 py-4 text-[#5C5C5C]">{v.location}</td>
                      <td className="px-5 py-4 text-[#5C5C5C]">{fmtDate(v.created_at)}</td>
                      <td className="px-5 py-4 text-[#5C5C5C]">{fmtDate(v.expires_at)}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1 flex-wrap">
                          <button onClick={() => setEditingVacancy(v)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#1E2A78] hover:bg-[#E6F3FE]" data-testid={`admin-vacancy-edit-${v.id}`} title="Edit">
                            <Edit2 size={14} /> Edit
                          </button>
                          <button onClick={() => refresh(v.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#1E2A78] hover:bg-[#E6F3FE]" data-testid={`admin-vacancy-refresh-${v.id}`} title="Refresh (extend by 30 days)">
                            <RefreshCw size={14} /> Refresh
                          </button>
                          <button onClick={() => shareOnLinkedIn(v)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#1E2A78] hover:bg-[#E6F3FE]" data-testid={`admin-vacancy-share-${v.id}`} title="Share on LinkedIn">
                            <Share2 size={14} /> LinkedIn
                          </button>
                          <button onClick={() => copyLink(v)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#1E2A78] hover:bg-[#E6F3FE]" data-testid={`admin-vacancy-copy-${v.id}`} title="Copy link">
                            <Link2 size={14} />
                          </button>
                          <button onClick={() => remove(v.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 hover:bg-red-50" data-testid={`admin-vacancy-delete-${v.id}`} title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === "cvs" && <List items={cvs} columns={[
          { key: "created_at", label: "Date", fmt },
          { key: "full_name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "specialism", label: "Specialism" },
          { key: "location", label: "Location" },
          { key: "cv_link", label: "CV link", render: (val) => val ? <a href={val} target="_blank" rel="noreferrer" className="text-[#1E2A78] link-underline">Open</a> : "—" },
        ]} empty="No CV submissions yet." testid="cvs-table" />}

        {tab === "applications" && <List items={applications} columns={[
          { key: "created_at", label: "Date", fmt },
          { key: "role_title", label: "Role" },
          { key: "company_name", label: "Company" },
          { key: "full_name", label: "Candidate" },
          { key: "email", label: "Email" },
          { key: "cv_link", label: "CV link", render: (val) => val ? <a href={val} target="_blank" rel="noreferrer" className="text-[#1E2A78] link-underline">Open</a> : "—" },
          { key: "cv_filename", label: "CV file", render: (val, row) => row?.cv_file_id ? (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/applications/${row.id}/cv`} className="inline-flex items-center gap-1 text-[#1E2A78] link-underline" download data-testid={`admin-cv-download-${row.id}`} onClick={(e) => {
              // attach Bearer token via fetch fallback since download anchors can't set headers
              e.preventDefault();
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/applications/${row.id}/cv`, { headers: authHeader() })
                .then(r => r.ok ? r.blob() : Promise.reject(r))
                .then(blob => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = val || 'cv'; a.click();
                  URL.revokeObjectURL(url);
                })
                .catch(() => toast.error("Download failed."));
            }}>
              <Download size={14} /> {val}
            </a>
          ) : "—" },
          { key: "cover_note", label: "Note", render: (val) => <span className="text-[#5C5C5C] line-clamp-2">{val || "—"}</span> },
        ]} empty="No applications yet." testid="applications-table" />}

        {tab === "insights" && (
          insights.length === 0 ? (
            <div className="bg-white border border-[#E2E0D9] p-10 text-center text-[#5C5C5C]" data-testid="insights-empty-state">
              No insights yet. Click <span className="text-[#1E2A78] font-semibold">Pull new insight</span> to fetch one.
            </div>
          ) : (
            <div className="bg-white border border-[#E2E0D9] overflow-x-auto" data-testid="admin-insights-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F4F3F0]">
                    <th className="text-left px-5 py-4 overline">Date</th>
                    <th className="text-left px-5 py-4 overline">Category</th>
                    <th className="text-left px-5 py-4 overline">Title</th>
                    <th className="text-left px-5 py-4 overline">Source</th>
                    <th className="text-left px-5 py-4 overline">Type</th>
                    <th className="text-left px-5 py-4 overline">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {insights.map((i) => (
                    <tr key={i.id} className="border-t border-[#E2E0D9] align-top" data-testid={`admin-insight-row-${i.id}`}>
                      <td className="px-5 py-4 text-[#5C5C5C]">{fmtDate(i.published_at)}</td>
                      <td className="px-5 py-4 text-[#5C5C5C]">{i.category}</td>
                      <td className="px-5 py-4 font-semibold max-w-md">{i.title}</td>
                      <td className="px-5 py-4 text-[#5C5C5C]">
                        {i.source_url ? <a href={i.source_url} target="_blank" rel="noreferrer" className="link-underline text-[#1E2A78]">{i.source}</a> : i.source}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[10px] tracking-[0.18em] uppercase font-semibold px-2 py-1 ${i.auto_published ? "bg-[#E6F3FE] text-[#1E2A78]" : "bg-[#F4F3F0] text-[#5C5C5C]"}`}>
                          {i.auto_published ? "Auto" : "Seed"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <button onClick={() => deleteInsight(i.id)} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 hover:bg-red-50" data-testid={`admin-insight-delete-${i.id}`} title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === "contacts" && <List items={contacts} columns={[
          { key: "created_at", label: "Date", fmt },
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "subject", label: "Subject" },
          { key: "message", label: "Message", render: (val) => <span className="text-[#5C5C5C] line-clamp-2">{val}</span> },
        ]} empty="No contacts yet." testid="contacts-table" />}

        {tab === "newsletter" && <List items={newsletter} columns={[
          { key: "created_at", label: "Date", fmt },
          { key: "email", label: "Email" },
        ]} empty="No subscribers yet." testid="newsletter-table" />}
      </main>

      {showForm && <VacancyForm onClose={() => setShowForm(false)} onSaved={loadAll} />}
      {editingVacancy && <VacancyForm initial={editingVacancy} onClose={() => setEditingVacancy(null)} onSaved={loadAll} />}
    </div>
  );
}

function List({ items, columns, empty, testid }) {
  if (!items?.length) {
    return <div className="bg-white border border-[#E2E0D9] p-10 text-center text-[#5C5C5C]" data-testid={`${testid}-empty`}>{empty}</div>;
  }
  return (
    <div className="bg-white border border-[#E2E0D9] overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#F4F3F0]">
            {columns.map((c) => (
              <th key={c.key} className="text-left px-5 py-4 overline">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t border-[#E2E0D9] align-top">
              {columns.map((c) => (
                <td key={c.key} className="px-5 py-4 text-[#222]">
                  {c.render ? c.render(it[c.key], it) : c.fmt ? c.fmt(it[c.key]) : (it[c.key] || "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
